/**
 * V165.5 — Blog Agent KPI Dashboard Tab
 *
 * Shows the user a high-level view of all their blog agents:
 *   • Cross-agent totals (agents, published articles, total views)
 *   • Per-agent KPI cards with mini-charts
 *   • Top performing article per agent
 *   • 14-day view trend chart for the selected agent
 *
 * Hooks into /api/blog-agents/stats-by-email + /api/blog-agent/{id}/kpi.
 * Auto-refreshes every 60 seconds while open.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Bot, FileText, Eye, TrendingUp, AlertCircle, ChevronRight,
  Loader2, Sparkles, Trophy,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { API } from "../../config";

const POLL_MS = 60_000;

export default function BlogAgentKPITab({ user, t = {} }) {
  const email = (user?.email || "").toLowerCase();
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Load cross-agent stats
  useEffect(() => {
    if (!email) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/blog-agents/stats-by-email`, {
          params: { email }, withCredentials: true,
        });
        if (alive) {
          setData(r.data);
          setLoading(false);
          // Auto-select first agent
          if (!selectedId && r.data?.per_agent?.length) {
            setSelectedId(r.data.per_agent[0].agent_id);
          }
        }
      } catch (e) {
        if (alive) {
          setErr(e?.response?.data?.detail || "Failed to load stats");
          setLoading(false);
        }
      }
    };
    load();
    const iv = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, [email]);  // eslint-disable-line

  // Load per-agent detail
  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    setDetailLoading(true);
    axios
      .get(`${API}/blog-agent/${selectedId}/kpi`, { withCredentials: true })
      .then((r) => alive && setDetail(r.data))
      .catch(() => alive && setDetail(null))
      .finally(() => alive && setDetailLoading(false));
    return () => { alive = false; };
  }, [selectedId]);

  const chartData = useMemo(() => {
    return detail?.daily_views_14d || [];
  }, [detail]);

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-gray-500 p-6" data-testid="bka-loading">
        <Loader2 className="w-5 h-5 animate-spin" /><span>{t.bka_loading || "Načítavam KPI…"}</span>
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-start gap-3" data-testid="bka-error">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-bold">{t.bka_err || "Chyba pri načítaní"}</p>
          <p className="text-xs mt-1">{err}</p>
        </div>
      </div>
    );
  }

  // No agents → activation CTA
  if (!data || data.agents === 0) {
    return (
      <div className="p-8 text-center bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-100 rounded-xl" data-testid="bka-empty">
        <Bot className="w-12 h-12 text-purple-600 mx-auto mb-4" />
        <h2 className="text-xl font-black text-gray-800 mb-2 font-['Chivo']">
          {t.bka_empty_h || "Žiadny aktívny Blog Agent"}
        </h2>
        <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
          {t.bka_empty_sub || "Spustite svojho prvého agenta — denne napíše a publikuje SEO článok pre vašu doménu. Stačí URL a téma."}
        </p>
        <a
          href="/dashboard?tab=blog-agent"
          className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-3 rounded-lg"
          data-testid="bka-cta-launch"
        >
          <Sparkles className="w-4 h-4" />
          {t.bka_empty_cta || "Spustiť Blog Agenta"}
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bka-dashboard">
      {/* Cross-agent totals */}
      <div className="grid grid-cols-3 gap-3">
        <KpiCell icon={Bot} label={t.bka_agents || "Aktívni agenti"} value={data.agents} accent="purple" testid="bka-total-agents" />
        <KpiCell icon={FileText} label={t.bka_published || "Publikované články"} value={data.totals.published} accent="blue" testid="bka-total-published" />
        <KpiCell icon={Eye} label={t.bka_views || "Celkové zobrazenia"} value={data.totals.views.toLocaleString()} accent="emerald" testid="bka-total-views" />
      </div>

      {/* Top performer banner */}
      {data.top_agent && data.top_agent.views > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3" data-testid="bka-top-agent">
          <Trophy className="w-6 h-6 text-amber-500 shrink-0" />
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
              {t.bka_top_agent || "Top performer tento týždeň"}
            </p>
            <p className="text-sm text-gray-800 font-bold">{data.top_agent.topic || data.top_agent.agent_id}</p>
            <p className="text-xs text-gray-600">
              {data.top_agent.published} {t.bka_articles || "článkov"} · <strong>{data.top_agent.views.toLocaleString()}</strong> {t.bka_views_short || "zobrazení"}
            </p>
          </div>
        </div>
      )}

      {/* Per-agent grid */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Agent list */}
        <div className="lg:col-span-1 space-y-2" data-testid="bka-agent-list">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
            {t.bka_my_agents || "Moji agenti"} ({data.per_agent.length})
          </h3>
          {data.per_agent.map((a) => (
            <button
              key={a.agent_id}
              onClick={() => setSelectedId(a.agent_id)}
              data-testid={`bka-agent-${a.agent_id}`}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                selectedId === a.agent_id
                  ? "bg-purple-50 border-purple-300"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-sm font-bold text-gray-800 truncate">{a.topic || a.agent_id}</p>
                <ChevronRight className={`w-4 h-4 shrink-0 ${selectedId === a.agent_id ? "text-purple-600" : "text-gray-400"}`} />
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span><FileText className="w-3 h-3 inline mr-1" />{a.published}</span>
                <span><Eye className="w-3 h-3 inline mr-1" />{a.views.toLocaleString()}</span>
                <StatusBadge status={a.status} />
              </div>
            </button>
          ))}
        </div>

        {/* Selected agent detail */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5" data-testid="bka-detail">
          {detailLoading ? (
            <div className="flex items-center gap-3 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">{t.bka_loading_detail || "Načítavam detail…"}</span></div>
          ) : !detail ? (
            <p className="text-sm text-gray-400">{t.bka_select || "Vyberte agenta zľava"}</p>
          ) : (
            <>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-800 font-['Chivo']">{detail.topic || detail.agent_id}</h3>
                  <p className="text-xs text-gray-500 mt-1 font-['JetBrains_Mono']">{detail.agent_id}</p>
                </div>
                <StatusBadge status={detail.status} />
              </div>

              <div className="grid grid-cols-4 gap-3 mb-5">
                <MiniCell label={t.bka_published || "Published"} value={detail.totals?.published || 0} />
                <MiniCell label={t.bka_remaining || "Remaining"} value={detail.totals?.remaining || 0} />
                <MiniCell label={t.bka_views || "Views"} value={(detail.totals?.views || 0).toLocaleString()} />
                <MiniCell label={t.bka_behind || "Behind"} value={detail.totals?.behind_schedule || 0} accent={detail.totals?.behind_schedule > 0 ? "rose" : "gray"} />
              </div>

              {/* 14-day chart */}
              <div className="mb-5">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                  {t.bka_14d_trend || "14-day view trend"}
                </p>
                <div style={{ width: "100%", height: 160 }} data-testid="bka-chart">
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="views" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top articles */}
              {Array.isArray(detail.top_articles) && detail.top_articles.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                    {t.bka_top_articles || "Top 5 articles"}
                  </p>
                  <ul className="space-y-1" data-testid="bka-top-articles">
                    {detail.top_articles.map((a, i) => (
                      <li key={a.slug || i} className="flex items-center justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
                        <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:text-purple-600 truncate flex-1">
                          {a.title}
                        </a>
                        <span className="text-xs font-bold text-gray-500 shrink-0">
                          <Eye className="w-3 h-3 inline mr-0.5" />{(a.views || 0).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const ACCENT = {
  purple: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-200" },
  blue:   { bg: "bg-blue-50",   text: "text-blue-600",   border: "border-blue-200" },
  emerald:{ bg: "bg-emerald-50",text: "text-emerald-600",border: "border-emerald-200" },
  rose:   { bg: "bg-rose-50",   text: "text-rose-600",   border: "border-rose-200" },
  gray:   { bg: "bg-gray-50",   text: "text-gray-600",   border: "border-gray-200" },
};

function KpiCell({ icon: Icon, label, value, accent = "gray", testid }) {
  const a = ACCENT[accent] || ACCENT.gray;
  return (
    <div className={`${a.bg} ${a.border} border rounded-lg p-4`} data-testid={testid}>
      <Icon className={`w-5 h-5 ${a.text} mb-2`} />
      <p className="text-2xl font-black text-gray-800 font-['Chivo']">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
}

function MiniCell({ label, value, accent = "gray" }) {
  const a = ACCENT[accent] || ACCENT.gray;
  return (
    <div className={`${a.bg} border ${a.border} rounded p-2.5`}>
      <p className={`text-lg font-black ${a.text}`}>{value}</p>
      <p className="text-[10px] uppercase text-gray-500 tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active:    { c: "bg-emerald-100 text-emerald-700", l: "Active" },
    pending_approval: { c: "bg-amber-100 text-amber-700",  l: "Pending" },
    completed: { c: "bg-gray-100 text-gray-600",  l: "Done" },
    paused:    { c: "bg-rose-100 text-rose-700",  l: "Paused" },
  };
  const m = map[status] || { c: "bg-gray-100 text-gray-600", l: status || "—" };
  return <span className={`${m.c} text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded`}>{m.l}</span>;
}
